import { z } from "zod";
import { apiBuilder, prefixApi } from "@zodios/core";

/*
{
    "name": "Scheduled Sync Action List",
    "description": "",
    "renders": [
        "application/json",
        "text/html"
    ],
    "parses": [
        "application/json",
        "application/x-www-form-urlencoded",
        "multipart/form-data"
    ],
    "actions": {
        "POST": {
            "id": {
                "type": "integer",
                "required": false,
                "read_only": true,
                "label": "ID"
            },
            "created_on": {
                "type": "datetime",
                "required": false,
                "read_only": true,
                "label": "Created on"
            },
            "updated_on": {
                "type": "datetime",
                "required": false,
                "read_only": true,
                "label": "Updated on"
            },
            "deleted_on": {
                "type": "datetime",
                "required": false,
                "read_only": true,
                "label": "Deleted on"
            },
            "is_enabled": {
                "type": "boolean",
                "required": false,
                "read_only": false,
                "label": "Is enabled"
            },
            "entity_id": {
                "type": "integer",
                "required": false,
                "read_only": false,
                "label": "Entity id",
                "min_value": 0,
                "max_value": 9223372036854775807
            },
            "action_type": {
                "type": "string",
                "required": true,
                "read_only": false,
                "label": "Action type"
            },
            "payload": {
                "type": "field",
                "required": false,
                "read_only": false,
                "label": "Payload"
            },
            "cron": {
                "type": "string",
                "required": false,
                "read_only": false,
                "label": "Cron"
            },
            "start_date": {
                "type": "datetime",
                "required": true,
                "read_only": false,
                "label": "Start date"
            },
            "end_date": {
                "type": "datetime",
                "required": false,
                "read_only": false,
                "label": "End date"
            },
            "next_run": {
                "type": "datetime",
                "required": false,
                "read_only": false,
                "label": "Next run"
            },
            "last_run": {
                "type": "datetime",
                "required": false,
                "read_only": false,
                "label": "Last run"
            },
            "run_cnt": {
                "type": "integer",
                "required": false,
                "read_only": false,
                "label": "Run cnt",
                "min_value": 0,
                "max_value": 2147483647
            },
            "ui_state": {
                "type": "field",
                "required": false,
                "read_only": false,
                "label": "Ui state"
            },
            "created_by": {
                "type": "field",
                "required": false,
                "read_only": true,
                "label": "Created by"
            },
            "updated_by": {
                "type": "field",
                "required": false,
                "read_only": true,
                "label": "Updated by"
            },
            "deleted_by": {
                "type": "field",
                "required": false,
                "read_only": true,
                "label": "Deleted by"
            },
            "org": {
                "type": "field",
                "required": true,
                "read_only": false,
                "label": "Org"
            },
            "content_type": {
                "type": "field",
                "required": false,
                "read_only": false,
                "label": "Content type"
            }
        }
    }
}
*/

const SchdeduleSyncActionCreationSchema = z.object({
  is_enabled: z.boolean().optional(),
  entity_id: z.string().optional(),
  action_type: z.string().optional(),
  cron: z.string().optional(),
  start_date: z.string().datetime().optional(),
  end_date: z.string().datetime().nullable().optional(),
  ui_state: z.any().optional(),
  content_type: z.any().optional(),
});

const SchdeduleSyncActionResponseSchema = z.object({
  id: z.number().int(),
  created_on: z.string().datetime(),
  updated_on: z.string().datetime(),
  deleted_on: z.string().datetime().nullable(),
  is_enabled: z.boolean(),
  entity_id: z.number().int().nullable(),
  action_type: z.string(),
  payload: z.any().nullable(),
  cron: z.string().nullable(),
  start_date: z.string().datetime(),
  end_date: z.string().datetime().nullable(),
  next_run: z.string().datetime().nullable(),
  last_run: z.string().datetime().nullable(),
  run_cnt: z.number().int(),
  ui_state: z.any().nullable(),
  created_by: z.any().nullable(),
  updated_by: z.any().nullable(),
  deleted_by: z.any().nullable(),
  org: z.number().int(),
  content_type: z.any().nullable(),
});

export const scheduleSyncActionsApiDefinitions = prefixApi(
  "/api/scheduled_sync_actions",
  apiBuilder({
    alias: "getScheduleSyncActions",
    method: "get",
    path: "/",
    status: 200,
    parameters: [
      {
        name: "page",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "entity_id",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "content_type",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "content_type__model",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.object({
      count: z.number(),
      next: z.string().nullable(),
      previous: z.string().nullable(),
      results: z.array(SchdeduleSyncActionResponseSchema),
    }),
  })
    .addEndpoint({
      alias: "createScheduleSyncAction",
      method: "post",
      path: "/",
      status: 201,
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: SchdeduleSyncActionCreationSchema,
        },
      ],
      response: z.object({
        ...SchdeduleSyncActionResponseSchema.shape,
      }),
    })
    .addEndpoint({
      alias: "updateScheduleSyncAction",
      method: "patch",
      path: "/:id/",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: z.object({
            id: z.number().int(),
            ...SchdeduleSyncActionCreationSchema.shape,
          }),
        },
      ],
      response: z.object({
        ...SchdeduleSyncActionResponseSchema.shape,
      }),
    })
    .build(),
);
